import React, { Fragment } from 'react';

import {
  Grid,
  Container,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box
} from '@material-ui/core';

import { makeStyles } from '@material-ui/core/styles';
import withWidth, { isWidthUp, isWidthDown } from '@material-ui/core/withWidth';

import { commonStyles } from '../../styles';
import { COPY_HEADER_MAIN, COPY_HEADER_SMALLS } from './maintenance_text';

import { HANDY_BLUE } from '../../utils/const.js.erb';

const useStyles = makeStyles(theme => ({
  container: {
    ...commonStyles(theme).container
  },
  bgLight: {
    background: theme.palette.background.default,
  },
  bgWhite: {
    background: '#ffffff',
  },
  wrapper: {
    paddingBottom: '26px',
    [theme.breakpoints.up('sm')]: {
      paddingBottom: '82px',
    },
    [theme.breakpoints.down('md')]: {
      paddingLeft: 0,
      paddingRight: 0,
    }
  },
  mediaCard: {
    boxShadow: 'unset',
  },
  mainCopy: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: '7px',
    [theme.breakpoints.up('md')]: {
      paddingTop: '93px',
      flexDirection: 'row'
    },
    justifyContent: 'space-between',
  },
  mainContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    paddingLeft: '16px',
    paddingRight: '16px',
    marginRight: '5em',
    [theme.breakpoints.up('sm')]: {
      paddingLeft: 0,
      paddingRight: 0,
    },
    [theme.breakpoints.up('md')]: {
      paddingLeft: '16px',
      paddingRight: '16px',
    },
  },
  secondaryContent: {
    paddingBottom: '3.5rem !important',
    marginLeft: 'auto',
    marginRight: 'auto',
    [theme.breakpoints.up('sm')]: {
      maxWidth: '368px',
    },
    [theme.breakpoints.up('md')]: {
      paddingLeft: 0,
      paddingRight: 0,
      paddingBottom: 0,
    },
  },
  mainImage: {
    order: 2,
    maxWidth: '468px',
    margin: 'auto',
    paddingLeft: '16px',
    paddingRight: '16px',
    [theme.breakpoints.down('xs')]: {
      height: 'auto',
    },
    [theme.breakpoints.up('sm')]: {
      paddingLeft: 0,
      paddingRight: 0,
    },
    [theme.breakpoints.up('md')]: {
      order: 0,
    },
  },
  smallImage: {
    paddingLeft: '16px',
    paddingRight: '16px',
    margin: 'auto',
    marginBottom: '19px',
    [theme.breakpoints.up('sm')]: {
      marginBottom: 0,
      paddingLeft: 0,
      paddingRight: 0,
      maxWidth: '368px',
    },
    [theme.breakpoints.up('md')]: {
      maxWidth: 'none',
    },
  },
  mainTitle: {
    fontWeight: 'bold',
    fontSize: '24px',
    lineHeight: '32px',
    letterSpacing: 'normal',
    marginBottom: '25px',
    color: theme.palette.text.light,
    [theme.breakpoints.up('sm')]: {
      fontSize: '32px',
      lineHeight: '35px',
      letterSpacing: '0.48px',
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: '8px',
    },
  },
  sectionSpacing: {
    paddingTop: '34px !important',
    paddingBottom: '15px !important',
    [theme.breakpoints.up('sm')]: {
      paddingTop: '39px !important',
      paddingBottom: '22px !important',
    }
  },
  subTitle: {
    fontWeight: 'bold',
    fontSize: '20px',
    letterSpacing: '0.41px',
    lineHeight: '24px',
    marginBottom: '15px',
    color: theme.palette.text.light,
    [theme.breakpoints.up('sm')]: {
      marginBottom: 0,
      fontSize: '22px',
      letterSpacing: '0.45px',
      lineHeight: '26px',
      paddingTop: '30px',
      paddingBottom: '18px',
    }
  },
  copyText: {
    fontWeight: '400',
    fontSize: '16px',
    letterSpacing: '-0.36px',
    lineHeight: '24px',
    color: theme.palette.text.light,
    [theme.breakpoints.up('sm')]: {
      fontSize: '18px',
      letterSpacing: '-0.4px',
      lineHeight: '27px',
    },
  },
  subText: {
    fontSize: '12px',
    color: theme.palette.text.light,
    lineHeight: '14px',
    marginTop: '20px',
    marginBottom: '20px'
  },
  itemText: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    fontSize: '20px',
    fontWeight: '800',
    '& .far, .fal, .fas, .fad': {
      content: '""',
      fontSize: '30px',
      marginRight: '0.5em',
      color: HANDY_BLUE,
    },
  }
}));

const Header = ({width}) => {
  const classes = useStyles();
  return (
    <Fragment>
      <Box className={`${classes.wrapper} ${classes.bgLight}`} px={3}>
        <Container className={classes.container}>
          <Grid container>
            {COPY_HEADER_MAIN.map( copy =>
              <Card key={copy.title} className={`${classes.mainCopy} ${classes.mediaCard} ${classes.bgLight}`}>
                <CardContent className={classes.mainContent}>
                  <Typography className={classes.mainTitle} gutterBottom component="h2" id="get_peace_of_mind_with_handy">
                    {copy.title}
                  </Typography>
                  <Typography className={classes.copyText} color="textSecondary" component="p">
                    {copy.text}
                  </Typography>
                  <Typography className={classes.subText} color="textSecondary" component="p">
                    {copy.subText}
                  </Typography>
                  {copy.subItems.map((item, idx) =>
                    <Typography key={idx} className={classes.itemText} color="textSecondary" component="span">
                      {item.text()}
                    </Typography>
                  )}
                </CardContent>
                <CardMedia
                  className={classes.mainImage}
                  component="img"
                  alt="Nest Wire install"
                  image={copy.img}
                  width='468'
                  height='339'
                />
              </Card>
            )}
          </Grid>
        </Container>
      </Box>

      <Box className={`${classes.wrapper} ${classes.bgWhite}`} px={3}>
        <Container className={classes.container}>
          <Grid container spacing={isWidthUp('md', width) ? 4 : 0}>
            <Grid className={classes.sectionSpacing} item xs={12} />
            {COPY_HEADER_SMALLS.map( (copy, i) =>
              <Grid item xs={12} md={4} key={copy.key}>
                <Card className={`${classes.mediaCard}`}>
                  {(isWidthUp('sm', width) || (isWidthDown('sm', width) && i < 1)) &&
                    <CardMedia
                      className={classes.smallImage}
                      component="img"
                      alt="Nest Wire install"
                      image={copy.img}
                      width='368'
                    />
                  }

                  <CardContent className={`${classes.mainContent} ${classes.secondaryContent}`}>
                    <Typography
                      align='center'
                      className={classes.subTitle}
                      gutterBottom
                      component="h2"
                    >
                      {copy.title}
                    </Typography>
                    <Typography align='center' className={classes.copyText} color="textSecondary" component="p">
                      {copy.text()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>
    </Fragment>
  );
};

export default withWidth()(Header);
